import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Undo2, Eye, FileText, Plus, Minus, GitCompare, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const actionColor = {
  create: 'bg-green-100 text-green-700 border-green-200',
  update: 'bg-blue-100 text-blue-700 border-blue-200',
  delete: 'bg-red-100 text-red-700 border-red-200',
  restore: 'bg-amber-100 text-amber-700 border-amber-200',
};

const AdminRevisions = () => {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Filter state
  const [actors, setActors] = useState([]);
  const [actorFilter, setActorFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');

  // Snapshot preview dialog (raw snapshot view)
  const [previewing, setPreviewing] = useState(null);
  const [previewData, setPreviewData] = useState(null);

  // Restore preview dialog (diff vs current)
  const [diffing, setDiffing] = useState(null);
  const [diffData, setDiffData] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const token = localStorage.getItem('admin_token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchRevisions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (actorFilter !== 'all') params.set('actor', actorFilter);
      if (entityFilter !== 'all') params.set('entity', entityFilter);
      if (actionFilter !== 'all') params.set('action', actionFilter);
      const qs = params.toString();
      const url = qs ? `${BACKEND_URL}/api/admin/revisions?${qs}` : `${BACKEND_URL}/api/admin/revisions`;
      const res = await axios.get(url, { headers: authHeaders });
      setRevisions(res.data || []);
    } catch (err) {
      toast.error('Failed to load revisions');
    } finally {
      setLoading(false);
    }
  };

  const fetchActors = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/revisions-meta/actors`, { headers: authHeaders });
      setActors(res.data || []);
    } catch {
      setActors([]);
    }
  };

  useEffect(() => {
    fetchActors();
  }, []);

  useEffect(() => {
    fetchRevisions();
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actorFilter, entityFilter, actionFilter]);

  const performRestore = async (rev) => {
    setRestoring(rev.version);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/revisions/${rev.version}/restore`, {}, { headers: authHeaders });
      toast.success(`Restored to v${rev.version}. New version: v${res.data.new_version}`);
      setDiffing(null);
      setDiffData(null);
      fetchRevisions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(null);
    }
  };

  const openDiff = async (rev) => {
    setDiffing(rev);
    setDiffData(null);
    setDiffLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/revisions/${rev.version}/diff`, { headers: authHeaders });
      setDiffData(res.data);
    } catch (err) {
      toast.error('Failed to load diff');
      setDiffing(null);
    } finally {
      setDiffLoading(false);
    }
  };

  const handlePreviewSnapshot = async (rev) => {
    setPreviewing(rev);
    setPreviewData(null);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/revisions/${rev.version}`, { headers: authHeaders });
      setPreviewData(res.data);
    } catch (err) {
      toast.error('Failed to load revision');
      setPreviewing(null);
    }
  };

  const renderDiffSection = (col, label) => {
    const d = diffData?.diff?.[col] || { added: [], removed: [], modified: [] };
    const total = d.added.length + d.removed.length + d.modified.length;
    return (
      <div className="border rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold capitalize">{label}</h4>
          <div className="flex gap-2 text-xs font-medium">
            <span className="text-green-700">+{d.added.length}</span>
            <span className="text-blue-700">~{d.modified.length}</span>
            <span className="text-red-700">-{d.removed.length}</span>
          </div>
        </div>
        {total === 0 ? (
          <p className="text-xs text-gray-400 italic">no changes</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {d.added.map((x) => (
              <li key={`a-${x.id}`} className="flex items-start gap-2 text-green-700">
                <Plus className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>Add back <strong>{x.name}</strong></span>
              </li>
            ))}
            {d.modified.map((x) => (
              <li key={`m-${x.id}`} className="flex items-start gap-2 text-blue-700">
                <GitCompare className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  Modify <strong>{x.name}</strong>
                  {x.changed_fields?.length > 0 && (
                    <span className="text-xs text-blue-500 ml-1">
                      ({x.changed_fields.slice(0, 4).join(', ')}{x.changed_fields.length > 4 ? '…' : ''})
                    </span>
                  )}
                </span>
              </li>
            ))}
            {d.removed.map((x) => (
              <li key={`r-${x.id}`} className="flex items-start gap-2 text-red-700">
                <Minus className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <span>Remove <strong>{x.name}</strong></span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Versions</h1>
        <p className="text-gray-600 text-sm">
          Every Products / Projects / Services change creates a new version. Click <strong>Preview Restore</strong> to see exactly what will change before rolling back.
          The last <strong>50</strong> versions are retained.
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-4" data-testid="admin-revisions-filters">
        <CardContent className="p-3 flex flex-wrap gap-3 items-center">
          <span className="text-xs font-semibold flex items-center gap-1 text-gray-600">
            <Filter className="h-3.5 w-3.5" />
            Filter
          </span>
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="admin-revisions-filter-actor">
              <SelectValue placeholder="Actor" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actors</SelectItem>
              {actors.map((a) => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={entityFilter} onValueChange={setEntityFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="admin-revisions-filter-entity">
              <SelectValue placeholder="Entity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="project">Projects</SelectItem>
              <SelectItem value="product">Products</SelectItem>
              <SelectItem value="service">Services</SelectItem>
            </SelectContent>
          </Select>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-36 h-9" data-testid="admin-revisions-filter-action">
              <SelectValue placeholder="Action" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              <SelectItem value="create">Create</SelectItem>
              <SelectItem value="update">Update</SelectItem>
              <SelectItem value="delete">Delete</SelectItem>
              <SelectItem value="restore">Restore</SelectItem>
            </SelectContent>
          </Select>
          {(actorFilter !== 'all' || entityFilter !== 'all' || actionFilter !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setActorFilter('all'); setEntityFilter('all'); setActionFilter('all'); }}
              className="h-9 text-xs"
              data-testid="admin-revisions-filter-clear"
            >
              Clear
            </Button>
          )}
          <span className="text-xs text-gray-500 ml-auto">
            {revisions.length} version{revisions.length === 1 ? '' : 's'} match
          </span>
        </CardContent>
      </Card>

      {loading ? (
        <p className="text-gray-500">Loading versions…</p>
      ) : revisions.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            No versions yet. Make a change to Products, Projects, or Services to create your first version.
          </CardContent>
        </Card>
      ) : (() => {
        const totalPages = Math.max(1, Math.ceil(revisions.length / PAGE_SIZE));
        const currentPage = Math.min(page, totalPages);
        const pagedRevisions = revisions.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
        return (
        <>
        <div className="space-y-2">
          {pagedRevisions.map((rev) => (
            <Card key={rev.version} data-testid={`admin-revisions-row-${rev.version}`}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="flex-shrink-0 w-16 text-center">
                  <div className="text-2xl font-bold" style={{ color: '#00CED1' }}>v{rev.version}</div>
                </div>
                <Badge variant="outline" className={`flex-shrink-0 capitalize ${actionColor[rev.action] || ''}`}>
                  {rev.action}
                </Badge>
                <div className="flex-grow min-w-0">
                  <p className="font-medium truncate">{rev.summary}</p>
                  <p className="text-xs text-gray-500">
                    {rev.actor || 'admin'} • {new Date(rev.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="icon" onClick={() => handlePreviewSnapshot(rev)} title="View snapshot">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => openDiff(rev)}
                    style={{ borderColor: '#00CED1', color: '#00CED1' }}
                    data-testid={`admin-revisions-preview-${rev.version}`}
                  >
                    <GitCompare className="h-4 w-4 mr-1" />
                    Preview Restore
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {revisions.length > PAGE_SIZE && (
          <div className="flex items-center justify-between mt-6" data-testid="admin-revisions-pagination">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, revisions.length)} of {revisions.length}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{ borderColor: '#00CED1', color: '#00CED1' }}
                data-testid="admin-revisions-prev-page"
              >
                Previous
              </Button>
              <span className="px-3 py-2 text-sm font-medium">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{ borderColor: '#00CED1', color: '#00CED1' }}
                data-testid="admin-revisions-next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
        </>
        );
      })()}

      {/* Restore-preview (diff) dialog */}
      <Dialog open={!!diffing} onOpenChange={(o) => !o && (setDiffing(null), setDiffData(null))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="restore-preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview restore to v{diffing?.version}</DialogTitle>
            <DialogDescription>
              {diffing?.summary} — {diffing && new Date(diffing.createdAt).toLocaleString()}
            </DialogDescription>
          </DialogHeader>

          {diffLoading || !diffData ? (
            <p className="text-gray-500 py-6 text-center">Computing diff…</p>
          ) : diffData.total_changes === 0 ? (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="p-6 text-center">
                <p className="text-green-800 font-semibold">No changes</p>
                <p className="text-sm text-green-700 mt-1">
                  Your current content already matches v{diffing.version}. Restoring would be a no-op.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <strong>{diffData.total_changes}</strong> change{diffData.total_changes === 1 ? '' : 's'} will be applied if you restore. This action creates a new version (v{(revisions[0]?.version ?? 0) + 1}) and is itself reversible.
              </div>
              <div className="space-y-3 mt-3">
                {renderDiffSection('products', 'Products')}
                {renderDiffSection('projects', 'Projects')}
                {renderDiffSection('services', 'Services')}
              </div>
            </>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setDiffing(null); setDiffData(null); }}>
              Cancel
            </Button>
            <Button
              onClick={() => performRestore(diffing)}
              disabled={restoring === diffing?.version || diffLoading || diffData?.total_changes === 0}
              className="text-white"
              style={{ backgroundColor: '#00CED1' }}
              data-testid={`admin-revisions-confirm-restore-${diffing?.version}`}
            >
              <Undo2 className="h-4 w-4 mr-1" />
              {restoring === diffing?.version ? 'Restoring…' : `Confirm Restore to v${diffing?.version}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Raw snapshot dialog (still available via the eye icon) */}
      <Dialog open={!!previewing} onOpenChange={(o) => !o && setPreviewing(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Snapshot at v{previewing?.version}</DialogTitle>
            <DialogDescription>
              {previewing?.summary} — {previewing && new Date(previewing.createdAt).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {!previewData ? (
            <p className="text-gray-500">Loading snapshot…</p>
          ) : (
            <div className="space-y-3 text-sm">
              {['products', 'projects', 'services'].map((col) => {
                const docs = previewData.snapshot?.[col] || [];
                return (
                  <div key={col}>
                    <div className="font-semibold capitalize mb-1">
                      {col} <span className="text-gray-400 text-xs">({docs.length})</span>
                    </div>
                    {docs.length === 0 ? (
                      <p className="text-gray-400 italic text-xs">empty</p>
                    ) : (
                      <ul className="text-xs text-gray-700 space-y-1 pl-4 list-disc">
                        {docs.slice(0, 30).map((d) => (
                          <li key={d.id}>{d.name || d.title}</li>
                        ))}
                        {docs.length > 30 && <li className="text-gray-400">…and {docs.length - 30} more</li>}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminRevisions;
